import { Column, Entity, Index, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientWholesalerEntity } from './client-wholesaler.entity';
import { WholesalerAliasEntity } from './wholesaler-alias.entity';

/**
 * A supplier our customers also buy from. SM1 calls these wholesalers (the
 * pharmacy "primary wholesaler"); SM2 shows them as Competitors (PD-17066).
 * The table is shared with SM1, which reads only `name` and `externalId`, so
 * every column added here is nullable or has a database default.
 *
 * The count, spend and date columns are stored summaries of this competitor's
 * customer links and logged prices, kept so the Competitors list can sort and
 * filter on them in SQL. CompetitorSummaryService refreshes them on every SM2
 * write and in a nightly reconcile, which also catches links SM1 and the ERP
 * imports add or hard-delete without going through SM2.
 */
@Index('wholesaler_pk', ['name'], { unique: true })
@Index('wholesaler_fk_vendor__id', ['fkVendorId'], {})
@Entity('wholesaler')
export class WholesalerEntity extends BaseEntity {
	@Column('varchar', { name: 'name', unique: true, length: 255 })
	public name!: string;

	@Column('varchar', { name: 'externalId', nullable: true, length: 255 })
	public externalId!: string | null;

	@Column('varchar', { name: 'website', nullable: true, length: 255 })
	public website!: string | null;

	@Column('varchar', { name: 'phone', nullable: true, length: 64 })
	public phone!: string | null;

	@Column('varchar', { name: 'address1', nullable: true, length: 255 })
	public address1!: string | null;

	@Column('varchar', { name: 'city', nullable: true, length: 255 })
	public city!: string | null;

	@Column('varchar', { name: 'state', nullable: true, length: 64 })
	public state!: string | null;

	@Column('varchar', { name: 'zip', nullable: true, length: 32 })
	public zip!: string | null;

	@Column('text', { name: 'note', nullable: true })
	public note!: string | null;

	/**
	 * Inactive competitors keep their links and prices but leave the main
	 * list. A tinyint, so the runtime value is 0/1; normalize before it reaches
	 * a response.
	 */
	@Column('tinyint', { name: 'isActive', width: 1, default: 1 })
	public isActive!: boolean;

	/** Set when this competitor is also one of our vendors ("also our vendor"). */
	@Column('int', { name: 'fk_vendor__id', nullable: true, unsigned: true })
	public fkVendorId!: number | null;

	/** Current (not former) customer links. */
	@Column('int', { name: 'customerCount', unsigned: true, default: 0 })
	public customerCount!: number;

	@Column('int', { name: 'primaryCount', unsigned: true, default: 0 })
	public primaryCount!: number;

	@Column('int', { name: 'secondaryCount', unsigned: true, default: 0 })
	public secondaryCount!: number;

	@Column('int', { name: 'alsoBuysCount', unsigned: true, default: 0 })
	public alsoBuysCount!: number;

	/** Sum of the estimated annual spend on current links; NULL when none carries an estimate. */
	@Column('decimal', {
		name: 'estimatedAnnualSpend',
		nullable: true,
		precision: 15,
		scale: 2,
	})
	public estimatedAnnualSpend!: string | null;

	/** How many current links carry an estimate, so the list can say "estimated on 30 of 42". */
	@Column('int', {
		name: 'estimatedCustomerCount',
		unsigned: true,
		default: 0,
	})
	public estimatedCustomerCount!: number;

	@Column('int', { name: 'priceCount', unsigned: true, default: 0 })
	public priceCount!: number;

	/** Distinct items among the logged prices ("126 prices on 80 items"). */
	@Column('int', { name: 'pricedItemCount', unsigned: true, default: 0 })
	public pricedItemCount!: number;

	@Column('datetime', { name: 'lastPriceDate', nullable: true })
	public lastPriceDate!: Date | null;

	@Column('datetime', { name: 'lastConfirmedDate', nullable: true })
	public lastConfirmedDate!: Date | null;

	@OneToMany(
		() => ClientWholesalerEntity,
		(clientWholesaler) => clientWholesaler.fkWholesaler,
		{},
	)
	public clientWholesalers!: Array<ClientWholesalerEntity>;

	@OneToMany(() => WholesalerAliasEntity, (alias) => alias.wholesaler, {})
	public aliases!: Array<WholesalerAliasEntity>;
}
